import React, { useCallback, useMemo, useState } from 'react';
import { datasetsApi, georefApi } from '../lib/api';
import { geojsonBounds } from '../lib/layer-tree';
import { removeLayerParam } from '../lib/map-url';
import { downloadBlob } from '../lib/export';
import { useOptionalToast } from '../components/Toast';
import TopographyPanel from '../components/TopographyPanel';
import { DatasetMapMenu } from '../components/map/DatasetMenu';
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle } from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle,
  AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from '../components/ui/alert-dialog';

const safeName = (s) => String(s || 'dataset').replace(/[^\w.-]+/g, '_').slice(0, 60);

// ── Pure helpers (unit-tested) ──────────────────────────────────────────────

/** Rename a dataset's layers: "Electrodes — old" -> "Electrodes — new"; a loaded file layer takes the name itself. */
export function renameDatasetLayers(layers, datasetId, name) {
  return layers.map((l) => {
    if (l.dataset?.id === datasetId) {
      const prefix = String(l.name || '').includes(' — ') ? String(l.name).split(' — ')[0] : null;
      return { ...l, name: prefix ? `${prefix} — ${name}` : name, dataset: { ...l.dataset, name } };
    }
    if (l.projectDatasetId === datasetId) return { ...l, name };
    return l;
  });
}

/** The map entry (with a dataset) a rendered MapLibre feature belongs to, or null. */
export function datasetEntryForFeature(entries, features) {
  for (const f of features || []) {
    const e = entries.find((x) => x.datasetId && x.sources?.includes(f.source));
    if (e) return e;
  }
  return null;
}

/** Union of the bounds of every layer of a dataset, or null. */
export function datasetBounds(layers, datasetId) {
  let b = null;
  layers.forEach((l) => {
    if (l.dataset?.id !== datasetId && l.projectDatasetId !== datasetId) return;
    const lb = geojsonBounds(l.geojson);
    if (!lb) return;
    b = b ? [[Math.min(b[0][0], lb[0][0]), Math.min(b[0][1], lb[0][1])], [Math.max(b[1][0], lb[1][0]), Math.max(b[1][1], lb[1][1])]] : lb;
  });
  return b;
}

// ── Hook ────────────────────────────────────────────────────────────────────

/**
 * Dataset actions for the right-click menu (lib/layer-menu.js `ds:*` items) on the Layers panel
 * and on the map: zoom, rename, edit location / rotate (GeorefDialog via `raw`), topography from
 * the DEM, pseudosection dock, dataset page, geometry export, remove from map, delete.
 * `element` holds the dialogs and the map-click menu; render it once in MapView.
 */
export function useDatasetMenu({ lyr, raw, ui, entries, mapRefHolder, navigate, searchParams, setSearchParams }) {
  const { showToast } = useOptionalToast();
  const [renameFor, setRenameFor] = useState(null); // { id, name, text, busy }
  const [deleteFor, setDeleteFor] = useState(null); // { id, name, busy }
  const [topoDataset, setTopoDataset] = useState(null);
  const [mapMenu, setMapMenu] = useState(null); // { at: {x, y}, entry }
  const { setResultLayers, setImportedLayers, resultLayers, importedLayers } = lyr;

  const removeFromMap = useCallback((id) => {
    setResultLayers((ls) => ls.filter((r) => r.dataset?.id !== id));
    setImportedLayers((ls) => ls.filter((l) => l.projectDatasetId !== id));
    const next = removeLayerParam(searchParams, id);
    if (next.get('dataset') === id) next.delete('dataset');
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
  }, [setResultLayers, setImportedLayers, searchParams, setSearchParams]);

  const actions = useMemo(() => ({
    zoom: (id) => {
      const b = datasetBounds([...resultLayers, ...importedLayers], id);
      const map = mapRefHolder.current?.current?.getMap?.();
      if (!map || !b) return;
      if (b[0][0] === b[1][0] && b[0][1] === b[1][1]) map.jumpTo({ center: b[0], zoom: 17 });
      else map.fitBounds(b, { padding: 60, duration: 600, maxZoom: 19 });
    },
    rename: (id, name) => setRenameFor({ id, name: name || '', text: name || '', busy: false }),
    editLocation: (id, name) => raw.openGeorefFor(id, name, undefined),
    rotate: (id, name) => raw.openGeorefFor(id, name, 'origin_azimuth'),
    topography: async (id) => {
      try {
        const res = await datasetsApi.get(id);
        const ds = res.data;
        if (!(ds?.metadata?.n_electrodes > 0)) { showToast('This dataset has no electrodes to sample', 'error'); return; }
        setTopoDataset(ds);
      } catch (e) {
        showToast('Could not open the topography panel', 'error');
      }
    },
    pseudosection: (id) => {
      const open = ui.pseudosection?.datasetId === id && !ui.pseudosection?.model;
      ui.setPseudosection(open ? null : { datasetId: id });
    },
    openPage: (id) => navigate(`/datasets/${id}`),
    exportGeometry: async (id, format, name) => {
      try {
        const res = await georefApi.exportGeometry(id, format, format === 'trn' ? 2 : undefined);
        downloadBlob(res.data, `${safeName(name)}.${format}`);
      } catch (e) {
        showToast('Geometry export failed', 'error');
      }
    },
    removeFromMap,
    deleteDataset: (id, name) => setDeleteFor({ id, name: name || id, busy: false }),
  }), [resultLayers, importedLayers, mapRefHolder, raw, ui, navigate, showToast, removeFromMap]);

  const submitRename = async () => {
    const { id, text, name } = renameFor;
    const next = text.trim();
    if (!next || next === name) { setRenameFor(null); return; }
    setRenameFor((r) => ({ ...r, busy: true }));
    try {
      await datasetsApi.update(id, { name: next });
      setResultLayers((ls) => renameDatasetLayers(ls, id, next));
      setImportedLayers((ls) => renameDatasetLayers(ls, id, next));
      showToast(`Renamed to "${next}"`, 'success');
      setRenameFor(null);
    } catch (e) {
      showToast(e?.response?.data?.detail || 'Could not rename the dataset', 'error');
      setRenameFor((r) => (r ? { ...r, busy: false } : r));
    }
  };

  const confirmDelete = async () => {
    const { id, name } = deleteFor;
    setDeleteFor((d) => ({ ...d, busy: true }));
    try {
      await datasetsApi.delete(id);
      removeFromMap(id);
      showToast(`Deleted "${name}"`, 'success');
      setDeleteFor(null);
    } catch (e) {
      showToast(e?.response?.data?.detail || 'Could not delete the dataset', 'error');
      setDeleteFor((d) => (d ? { ...d, busy: false } : d));
    }
  };

  /** Map `contextmenu` handler: opens the menu only over a dataset feature (and then blocks the browser menu). */
  const onMapContextMenu = useCallback((evt) => {
    const map = evt.target;
    if (!map?.queryRenderedFeatures || !evt.point) return;
    const { x, y } = evt.point;
    const entry = datasetEntryForFeature(entries, map.queryRenderedFeatures([[x - 5, y - 5], [x + 5, y + 5]]));
    if (!entry) return;
    evt.originalEvent?.preventDefault?.();
    setMapMenu({ at: { x: evt.originalEvent.clientX, y: evt.originalEvent.clientY }, entry });
  }, [entries]);

  const element = (
    <>
      <DatasetMapMenu at={mapMenu?.at} entry={mapMenu?.entry} datasetActions={actions} onClose={() => setMapMenu(null)} />
      <Dialog open={!!renameFor} onOpenChange={(o) => { if (!o) setRenameFor(null); }}>
        <DialogContent data-testid="dataset-rename-dialog">
          <form onSubmit={(e) => { e.preventDefault(); submitRename(); }}>
            <DialogHeader><DialogTitle>Rename dataset</DialogTitle></DialogHeader>
            <input autoFocus aria-label="Dataset name" data-testid="dataset-rename-input" value={renameFor?.text ?? ''}
              onFocus={(e) => e.target.select()} onChange={(e) => setRenameFor((r) => ({ ...r, text: e.target.value }))}
              style={{ width: '100%', margin: '12px 0', height: 32, fontSize: 13, padding: '0 8px', borderRadius: 6, color: 'var(--gs-text-0)', background: 'var(--gs-bg-4)', border: '1px solid var(--gs-border)', outline: 'none' }} />
            <DialogFooter>
              <button type="button" onClick={() => setRenameFor(null)} className="px-3 py-1 text-sm border rounded-md">Cancel</button>
              <button type="submit" disabled={renameFor?.busy || !renameFor?.text.trim()} data-testid="dataset-rename-save"
                className="px-3 py-1 text-sm rounded-md bg-primary text-primary-foreground disabled:opacity-50">Save</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <AlertDialog open={!!deleteFor} onOpenChange={(o) => { if (!o && !deleteFor?.busy) setDeleteFor(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete dataset?</AlertDialogTitle>
            <AlertDialogDescription>{deleteFor ? `"${deleteFor.name}" and its stored files will be deleted from the project. This cannot be undone.` : ''}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={deleteFor?.busy} onClick={(e) => { e.preventDefault(); confirmDelete(); }}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {topoDataset && <TopographyPanel dataset={topoDataset} onClose={() => setTopoDataset(null)} />}
    </>
  );

  return { actions, element, onMapContextMenu };
}
